import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { ACTIONS_TAB_LABELS } from '../constants/actionsTabs';
import type { FilterState } from '../hooks/useFilters';
import {
  parseCommitmentDate,
  parseCommittedDate,
  isDateInRange,
  epochForSortDue,
  epochForSortCommitted,
  epochForSortApproved,
} from './dateUtils';
import { hasUnansweredNudgesForIssuer, isAwaitingBadgeRerequestAfterClarify } from './notificationActionSync';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

function resolveCommitmentPartyId(
  party?: { _id?: unknown; id?: unknown } | null
): string | undefined {
  if (!party) return undefined;
  const id = party._id ?? party.id;
  if (id == null || id === "") return undefined;
  return String(id).trim();
}

/** Rows from `myCommitments` may omit committer; don't drop them on portfolio. */
export function isOwnedByUserCommitter(
  commitment: { committer?: { _id?: string } },
  userId?: string | null
): boolean {
  if (!userId) return true;
  const committerId = commitment.committer?._id;
  if (!committerId) return true;
  return String(committerId) === String(userId);
}

/**
 * Nudge Requests are for the committer answering questions — not the issuer
 * reviewing someone else's badge request (e.g. John → Alex TEAMWORK).
 */
export function isUserCommitterForNudgeActions(
  commitment: {
    committer?: { _id?: unknown; id?: unknown };
    issuer?: { _id?: unknown; id?: unknown };
  },
  userId?: string | null
): boolean {
  if (!userId) return true;
  const uid = String(userId).trim();
  const committerId = resolveCommitmentPartyId(commitment.committer);
  const issuerId = resolveCommitmentPartyId(commitment.issuer);
  if (committerId) return committerId === uid;
  if (issuerId && issuerId === uid) return false;
  return isOwnedByUserCommitter(
    commitment as { committer?: { _id?: string } },
    userId
  );
}

export interface Commitment {
  id: number;
  /** Mongo/API id when present on transformed rows (not the UI list key). */
  _id?: string;
  title: string;
  dueDate: string;
  description: string;
  assignee: string;
  selected?: boolean;
  committedDate?: string;
  approvedDate?: string;
  completedDate?: string;
  type?: string;
  nudgesLeft?: number;
  totalNudges?: number;
  isExternal?: boolean;
  questions?: string[];
  explanation?: string;
  /** Promises Owed to Me — from API / extraData (see Actions transforms) */
  explanations?: string[];
  expectations?: string[];
  questionsAsked?: string[];
  questionsAnswered?: string[];
  responses?: { date: string; answer: string; questions?: string[] }[];
  isOverdue?: boolean;
  /** API commitment status (e.g. pending_approval when the member has requested a badge). */
  status?: string;
  badge?: { type?: string; name?: string };
}

/** Issuer actions for "Promises Owed to Me" on Actions. Clarify only after a badge request (pending_approval). */
export function getOwedToMeIssuerActionFlags(item: {
  completedDate?: string;
  badge?: { type?: string };
  status?: string;
  nudgesLeft?: number;
  totalNudges?: number;
  nudgesToAnswer?: number;
  questionsAnswered?: unknown;
  responses?: unknown;
  explanations?: string[];
  expectations?: string[];
  explanation?: string;
  extraData?: {
    nudgesToAnswer?: number;
    questionsAnswered?: unknown;
    explanations?: string[];
    expectations?: string[];
    explanation?: string;
  };
}): {
  showIssueButton: boolean;
  showRejectButton: boolean;
  showClarifyButton: boolean;
} {
  if (isAwaitingBadgeRerequestAfterClarify(item)) {
    return {
      showIssueButton: false,
      showRejectButton: false,
      showClarifyButton: false,
    };
  }

  const isP2PBadge = (item.badge as { type?: string } | undefined)?.type === "p2p";
  const completedDateValue = item.completedDate;
  const hasCompletedDate =
    !!completedDateValue &&
    (typeof completedDateValue === "string"
      ? completedDateValue.length > 0
      : String(completedDateValue).length > 0);
  const statusLower = String(item.status || "").toLowerCase();
  const isPendingBadgeApproval =
    statusLower === "pending_approval" || statusLower === "pending-approval";

  let showIssueButton = false;
  let showRejectButton = false;
  let showClarifyButton = false;

  if (isP2PBadge) {
    if (hasCompletedDate) {
      showIssueButton = true;
      showRejectButton = true;
    } else {
      showRejectButton = true;
    }
  } else if (isPendingBadgeApproval && hasCompletedDate) {
    showIssueButton = true;
    showRejectButton = true;
    showClarifyButton = true;
  } else {
    showIssueButton = true;
    showRejectButton = true;
  }

  // Nudge / p2p badges: Issue only after every committed nudge is answered.
  if (hasUnansweredNudgesForIssuer(item)) {
    showIssueButton = false;
  }

  return { showIssueButton, showRejectButton, showClarifyButton };
}

export interface FilterOptions {
  personFilter?: string;
  dateFilter?: string;
  searchTerm?: string;
  dateRange?: [Dayjs | null, Dayjs | null];
  badgeFilters?: string[];
  commitmentTextFilter?: string;
  assigneeFilter?: string;
  dueDateTableFilter?: Dayjs | null;
  committedDateTableFilter?: Dayjs | null;
  approvedDateTableFilter?: Dayjs | null;
}

/** Normalize badge labels for portfolio Badge Type filter matching. */
export function normalizeBadgeFilterLabel(name: string): string {
  return String(name || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

/**
 * Portfolio Badge Type filter: empty selection = all badges.
 * Matches commitment.badge.name (case-insensitive).
 */
export function commitmentMatchesBadgeFilter(
  commitment: {
    badge?: { name?: string } | null;
    badgeName?: string;
    title?: string;
  },
  selectedBadges: string[]
): boolean {
  if (!selectedBadges.length) return true;
  const name = normalizeBadgeFilterLabel(
    commitment.badge?.name || commitment.badgeName || ""
  );
  if (!name) return false;
  return selectedBadges.some(
    (badge) => normalizeBadgeFilterLabel(badge) === name
  );
}

export function formatPersonName(
  person?: {
    firstName?: string;
    lastName?: string;
    name?: string;
  } | null
): string {
  if (!person) return "Unknown";
  const fromParts = [person.firstName, person.lastName]
    .filter((part) => part != null && String(part).trim() !== "")
    .join(" ")
    .trim();
  return (
    fromParts ||
    (person.name != null ? String(person.name).trim() : "") ||
    "Unknown"
  );
}

const normalizePersonLabel = (value: string): string =>
  value.trim().replace(/\s+/g, " ").toLowerCase();

export const filterCommitmentsByPerson = (
  commitments: Commitment[],
  personFilter: string,
  groupMembers: { [key: string]: string[] }
): Commitment[] => {
  if (!personFilter) return commitments;

  if (personFilter === "External") {
    return commitments.filter((c) => c.isExternal);
  }

  if (groupMembers[personFilter]) {
    const members = new Set(
      groupMembers[personFilter].map((name) => normalizePersonLabel(name))
    );
    return commitments.filter((c) =>
      members.has(normalizePersonLabel(c.assignee || ""))
    );
  }

  const target = normalizePersonLabel(personFilter);
  return commitments.filter(
    (c) => normalizePersonLabel(c.assignee || "") === target
  );
};

export const filterCommitmentsByDate = (
  commitments: Commitment[],
  dateFilter: string
): Commitment[] => {
  if (!dateFilter || dateFilter === 'All') return commitments;

  const today = dayjs().startOf('day');
  const startOfWeek = dayjs().startOf('week');
  const endOfWeek = dayjs().endOf('week');

  return commitments.filter((c) => {
    const dueDate = parseCommitmentDate(c.dueDate);
    if (!dueDate) {
      return false;
    }

    // Normalize to start of day for accurate comparison
    const dueDateStart = dueDate.startOf('day');

    if (dateFilter === 'Today') {
      return dueDateStart.isSame(today, 'day');
    }

    if (dateFilter === 'This Week') {
      // Use inclusive comparison with day precision
      const isInWeek = dueDateStart.isSameOrAfter(startOfWeek, 'day') && 
                       dueDateStart.isSameOrBefore(endOfWeek, 'day');
      return isInWeek;
    }

    // Custom Range is handled separately via dateRange filter
    return false;
  });
};

export const filterCommitmentsBySearch = (
  commitments: Commitment[],
  searchTerm: string
): Commitment[] => {
  if (!searchTerm) return commitments;

  const lowerSearch = searchTerm.toLowerCase();
  return commitments.filter(
    (c) =>
      c.title.toLowerCase().includes(lowerSearch) ||
      c.description.toLowerCase().includes(lowerSearch) ||
      c.assignee.toLowerCase().includes(lowerSearch)
  );
};

export const filterCommitmentsByDateRange = (
  commitments: Commitment[],
  dateRange: [Dayjs | null, Dayjs | null]
): Commitment[] => {
  // Only apply filter if at least one date is set
  if (!dateRange || (!dateRange[0] && !dateRange[1])) return commitments;

  return commitments.filter((c) => {
    const dueDate = parseCommitmentDate(c.dueDate);
    if (!dueDate) {
      return false;
    }
    
    // Normalize to start of day for accurate comparison
    const dueDateStart = dueDate.startOf('day');
    const inRange = isDateInRange(dueDateStart, dateRange);
    return inRange;
  });
};

export const sortCommitments = (
  commitments: Commitment[],
  sortBy: string
): Commitment[] => {
  const sorted = [...commitments];

  switch (sortBy) {
    case 'dueDateOldest':
      return sorted.sort((a, b) => {
        const dateA = parseCommitmentDate(a.dueDate);
        const dateB = parseCommitmentDate(b.dueDate);
        if (!dateA) return 1;
        if (!dateB) return -1;
        return dateA.valueOf() - dateB.valueOf();
      });

    case 'dueDateNewest':
      return sorted.sort((a, b) => {
        const dateA = parseCommitmentDate(a.dueDate);
        const dateB = parseCommitmentDate(b.dueDate);
        if (!dateA) return 1;
        if (!dateB) return -1;
        return dateB.valueOf() - dateA.valueOf();
      });

    case 'committedDateOldest':
      return sorted.sort((a, b) => {
        const dateA = parseCommittedDate(a.committedDate);
        const dateB = parseCommittedDate(b.committedDate);
        if (!dateA) return 1;
        if (!dateB) return -1;
        return dateA.valueOf() - dateB.valueOf();
      });

    case 'committedDateNewest':
      return sorted.sort((a, b) => {
        const dateA = parseCommittedDate(a.committedDate);
        const dateB = parseCommittedDate(b.committedDate);
        if (!dateA) return 1;
        if (!dateB) return -1;
        return dateB.valueOf() - dateA.valueOf();
      });

    case 'alphabetical':
      return sorted.sort((a, b) => a.title.localeCompare(b.title));

    case 'approvedDateNewest':
      return sorted.sort((a, b) => {
        // approvedDate might be in format "Approved Dec 15, 2024" or just a date string
        const dateA = a.approvedDate ? parseCommitmentDate(a.approvedDate.replace('Approved ', '')) : null;
        const dateB = b.approvedDate ? parseCommitmentDate(b.approvedDate.replace('Approved ', '')) : null;
        if (!dateA) return 1;
        if (!dateB) return -1;
        return dateB.valueOf() - dateA.valueOf();
      });

    case 'approvedDateOldest':
      return sorted.sort((a, b) => {
        // approvedDate might be in format "Approved Dec 15, 2024" or just a date string
        const dateA = a.approvedDate ? parseCommitmentDate(a.approvedDate.replace('Approved ', '')) : null;
        const dateB = b.approvedDate ? parseCommitmentDate(b.approvedDate.replace('Approved ', '')) : null;
        if (!dateA) return 1;
        if (!dateB) return -1;
        return dateA.valueOf() - dateB.valueOf();
      });

    case 'overdue':
      // "Overdue" acts as a filter + sort: keep only overdue items, oldest due first.
      return sorted
        .filter((c) => {
          if (c.isOverdue) return true;
          const d = parseCommitmentDate(c.dueDate);
          return !!d && d.isBefore(dayjs(), 'day');
        })
        .sort((a, b) => {
          const dateA = parseCommitmentDate(a.dueDate);
          const dateB = parseCommitmentDate(b.dueDate);
          if (!dateA) return 1;
          if (!dateB) return -1;
          return dateA.valueOf() - dateB.valueOf();
        });

    case 'nudges':
      return sorted.sort((a, b) => {
        // Nudges first
        const aIsNudge = a.type === 'nudge';
        const bIsNudge = b.type === 'nudge';
        if (aIsNudge && !bIsNudge) return -1;
        if (!aIsNudge && bIsNudge) return 1;
        // Then sort by due date (newest first)
        const dateA = parseCommitmentDate(a.dueDate);
        const dateB = parseCommitmentDate(b.dueDate);
        if (!dateA) return 1;
        if (!dateB) return -1;
        return dateB.valueOf() - dateA.valueOf();
      });

    default:
      return sorted;
  }
};

export const applyAllFilters = (
  commitments: Commitment[],
  options: FilterOptions,
  groupMembers: { [key: string]: string[] } = {}
): Commitment[] => {
  let filtered = commitments;

  if (options.personFilter) {
    filtered = filterCommitmentsByPerson(filtered, options.personFilter, groupMembers);
  }

  if (options.dateFilter && options.dateFilter !== 'All' && options.dateFilter !== 'Custom Range') {
    filtered = filterCommitmentsByDate(filtered, options.dateFilter);
  }

  if (options.searchTerm) {
    filtered = filterCommitmentsBySearch(filtered, options.searchTerm);
  }

  if (options.dateRange) {
    filtered = filterCommitmentsByDateRange(filtered, options.dateRange);
  }

  if (options.badgeFilters && options.badgeFilters.length > 0) {
    const selected = new Set(
      options.badgeFilters.map((b) => b.toLowerCase().trim())
    );
    filtered = filtered.filter((c) =>
      selected.has(c.title.toLowerCase().trim())
    );
  }

  if (options.commitmentTextFilter) {
    const needle = options.commitmentTextFilter.toLowerCase();
    filtered = filtered.filter((c) =>
      (c.description || c.title || "").toLowerCase().includes(needle)
    );
  }

  if (options.assigneeFilter) {
    filtered = filtered.filter((c) =>
      c.assignee.toLowerCase().includes(options.assigneeFilter!.toLowerCase())
    );
  }

  if (options.dueDateTableFilter) {
    const dueTarget = options.dueDateTableFilter.startOf("day");
    filtered = filtered.filter((c) => {
      const d = parseCommitmentDate(c.dueDate);
      return !!d && d.startOf("day").isSame(dueTarget, "day");
    });
  }

  if (options.committedDateTableFilter) {
    const committedTarget = options.committedDateTableFilter.startOf("day");
    filtered = filtered.filter((c) => {
      const d = parseCommittedDate(c.committedDate);
      return !!d && d.startOf("day").isSame(committedTarget, "day");
    });
  }

  if (options.approvedDateTableFilter) {
    const approvedTarget = options.approvedDateTableFilter.startOf("day");
    filtered = filtered.filter((c) => {
      const raw = c.approvedDate?.replace(/^Approved\s+/i, "").trim();
      const d = raw ? parseCommitmentDate(raw) : null;
      return !!d && d.startOf("day").isSame(approvedTarget, "day");
    });
  }

  return filtered;
};

/** Classic Actions tabular mode: sort by clicked column (matches CommitmentsTableHeader / Row semantics). */
export type ClassicTableSortKey =
  | "badge"
  | "commitment"
  | "assignee"
  | "firstDate"
  | "secondDate"
  | "approved";

export interface ClassicTableSortContext {
  isBadgeRequestsTab: boolean;
  isRequestsToCommitTab: boolean;
  isAwaitingResponseTab: boolean;
  isMyBadgesTab: boolean;
  isBadgesIssuedTab: boolean;
}

const tieBreakIds = (a: Commitment, b: Commitment): number =>
  String(a.id).localeCompare(String(b.id), undefined, { numeric: true });

export function sortCommitmentsByClassicColumn(
  commitments: Commitment[],
  key: ClassicTableSortKey,
  direction: "asc" | "desc",
  ctx: ClassicTableSortContext
): Commitment[] {
  const sorted = [...commitments];
  const strCmp = (a: string, b: string) => {
    const p = a.localeCompare(b);
    return direction === "asc" ? p : -p;
  };

  switch (key) {
    case "badge":
      return sorted.sort((a, b) => {
        const p = strCmp(a.title || "", b.title || "");
        return p !== 0 ? p : tieBreakIds(a, b);
      });
    case "commitment":
      return sorted.sort((a, b) => {
        const p = strCmp(a.description || "", b.description || "");
        return p !== 0 ? p : tieBreakIds(a, b);
      });
    case "assignee":
      return sorted.sort((a, b) => {
        const p = strCmp(a.assignee || "", b.assignee || "");
        return p !== 0 ? p : tieBreakIds(a, b);
      });
    case "firstDate":
      return sorted.sort((a, b) => {
        const va = ctx.isBadgeRequestsTab
          ? epochForSortDue(a.dueDate)
          : epochForSortCommitted(a.committedDate);
        const vb = ctx.isBadgeRequestsTab
          ? epochForSortDue(b.dueDate)
          : epochForSortCommitted(b.committedDate);
        const diff = va - vb;
        const p = diff !== 0 ? (direction === "asc" ? diff : -diff) : 0;
        return p !== 0 ? p : tieBreakIds(a, b);
      });
    case "secondDate":
      return sorted.sort((a, b) => {
        const va = ctx.isBadgeRequestsTab
          ? epochForSortCommitted(a.committedDate)
          : epochForSortDue(a.dueDate);
        const vb = ctx.isBadgeRequestsTab
          ? epochForSortCommitted(b.committedDate)
          : epochForSortDue(b.dueDate);
        const diff = va - vb;
        const p = diff !== 0 ? (direction === "asc" ? diff : -diff) : 0;
        return p !== 0 ? p : tieBreakIds(a, b);
      });
    case "approved":
      return sorted.sort((a, b) => {
        const va = epochForSortApproved(a.approvedDate);
        const vb = epochForSortApproved(b.approvedDate);
        const diff = va - vb;
        const p = diff !== 0 ? (direction === "asc" ? diff : -diff) : 0;
        return p !== 0 ? p : tieBreakIds(a, b);
      });
    default:
      return sorted;
  }
}

export interface CommitmentTableFiltersState {
  badge: string[];
  commitmentText: string;
  assignee: string;
  dueDate: Dayjs | null;
  committedDate: Dayjs | null;
  approvedDate: Dayjs | null;
}

export interface CommitmentDisplayPipelineOptions {
  displayMode: 'regular' | 'table';
  filters: FilterState;
  tableFilters: CommitmentTableFiltersState;
  tableColumnSortMode: boolean;
  columnSort: { key: ClassicTableSortKey; direction: 'asc' | 'desc' } | null;
}

function classicSortContextForTabLabel(label: string): ClassicTableSortContext {
  const isOffersOrPending =
    label === ACTIONS_TAB_LABELS.RECEIVED_PROMISE_OFFERS ||
    label === ACTIONS_TAB_LABELS.BADGE_REQUESTS ||
    label === ACTIONS_TAB_LABELS.AWAITING_BADGE_APPROVAL;
  return {
    isBadgeRequestsTab: isOffersOrPending,
    isRequestsToCommitTab: label === ACTIONS_TAB_LABELS.RECEIVED_INVITATIONS,
    isAwaitingResponseTab: label === ACTIONS_TAB_LABELS.AWAITING_RESPONSE,
    isMyBadgesTab: label === 'My Badges',
    isBadgesIssuedTab: label === 'Badges Issued',
  };
}

function overdueFlagsForTabLabel(label: string) {
  return {
    isUnkeptTab: label.includes('Unkept'),
    isMyBadgesTab: label === 'My Badges',
    isBadgesIssuedTab: label === 'Badges Issued',
  };
}

/** Apply toolbar + column filters and sort for each tab (matches CommitmentsSection UI). */
export function buildFilteredCommitmentsByTab(
  tabs: { label: string; items: Commitment[] }[],
  options: CommitmentDisplayPipelineOptions,
  groupMembers: { [key: string]: string[] } = {}
): Record<string, Commitment[]> {
  const {
    displayMode,
    filters,
    tableFilters,
    tableColumnSortMode,
    columnSort,
  } = options;

  const result: Record<string, Commitment[]> = {};

  for (const tab of tabs) {
    const overdueFlags = overdueFlagsForTabLabel(tab.label);
    const processed = tab.items.map((item) => ({
      ...item,
      isOverdue:
        item.isOverdue ||
        (!overdueFlags.isUnkeptTab &&
          !overdueFlags.isMyBadgesTab &&
          !overdueFlags.isBadgesIssuedTab &&
          (parseCommitmentDate(item.dueDate)
            ? parseCommitmentDate(item.dueDate)!.isBefore(dayjs(), 'day')
            : false)),
    }));

    const filtered = applyAllFilters(
      processed,
      {
        personFilter: filters.person,
        dateFilter: filters.date,
        searchTerm: filters.searchTerm,
        dateRange: filters.dateRange,
        badgeFilters:
          displayMode === 'table' && tableFilters.badge.length > 0
            ? tableFilters.badge
            : undefined,
        commitmentTextFilter:
          displayMode === 'table' ? tableFilters.commitmentText : undefined,
        assigneeFilter:
          displayMode === 'table' ? tableFilters.assignee : undefined,
        dueDateTableFilter:
          displayMode === 'table' ? tableFilters.dueDate : null,
        committedDateTableFilter:
          displayMode === 'table' ? tableFilters.committedDate : null,
        approvedDateTableFilter:
          displayMode === 'table' ? tableFilters.approvedDate : null,
      },
      groupMembers
    );

    let sorted: Commitment[];
    if (tableColumnSortMode && columnSort) {
      sorted = sortCommitmentsByClassicColumn(
        filtered,
        columnSort.key,
        columnSort.direction,
        classicSortContextForTabLabel(tab.label)
      );
    } else {
      sorted = sortCommitments(filtered, filters.sortBy);
      if (filters.sortBy !== 'nudges' && filters.sortBy !== 'overdue') {
        sorted = sorted
          .map((item, index) => ({ item, index }))
          .sort((a, b) => {
            const aIsNudge = a.item.type === 'nudge';
            const bIsNudge = b.item.type === 'nudge';
            if (aIsNudge && !bIsNudge) return -1;
            if (!aIsNudge && bIsNudge) return 1;
            return a.index - b.index;
          })
          .map(({ item }) => item);
      }
    }

    result[tab.label] = sorted;
  }

  return result;
}

/** Merge per-tab filtered rows into export wizard data (fallback to raw until filters sync). */
export function mergeFilteredExportDataSources(
  rawMy: Record<string, Commitment[]>,
  rawOthers: Record<string, Commitment[]>,
  filteredMy: Record<string, Commitment[]>,
  filteredOthers: Record<string, Commitment[]>
): {
  myCommitments: Record<string, Commitment[]>;
  othersCommitments: Record<string, Commitment[]>;
} {
  const pick = (
    raw: Record<string, Commitment[]>,
    filtered: Record<string, Commitment[]>
  ) =>
    Object.fromEntries(
      Object.keys(raw).map((key) => [key, filtered[key] ?? raw[key]])
    ) as Record<string, Commitment[]>;

  return {
    myCommitments: pick(rawMy, filteredMy),
    othersCommitments: pick(rawOthers, filteredOthers),
  };
}

/** True when tab keys and row ids/order match (avoids export callback re-render loops). */
export function exportFilteredSnapshotsEqual(
  a: Record<string, Commitment[]>,
  b: Record<string, Commitment[]>
): boolean {
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  for (const key of keysA) {
    if (!Object.prototype.hasOwnProperty.call(b, key)) return false;
    const listA = a[key] ?? [];
    const listB = b[key] ?? [];
    if (listA.length !== listB.length) return false;
    for (let i = 0; i < listA.length; i++) {
      if (listA[i].id !== listB[i].id) return false;
    }
  }
  return true;
}
